import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { taxService } from '../../services/taxService';
import { format, isToday, isFuture, differenceInDays } from 'date-fns';
import { CalendarIcon, Clock, AlertCircle } from 'lucide-react';

const TaxCalendar = () => {
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    const taxEvents = taxService.getTaxCalendarEvents();
    setEvents(taxEvents);
    
    // Filter for upcoming events in the next 90 days
    const now = new Date();
    const upcoming = taxEvents
      .filter(event => {
        return isFuture(event.date) && differenceInDays(event.date, now) <= 90;
      })
      .sort((a, b) => a.date - b.date)
      .slice(0, 3);
      
    setUpcomingEvents(upcoming);
  }, []);
  
  // Helper function to determine urgency class based on date
  const getUrgencyClass = (date) => {
    const now = new Date();
    const days = differenceInDays(date, now);
    
    // Use theme-aware colors, keeping semantic meaning
    if (days <= 7) return 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'; // Red for urgent
    if (days <= 30) return 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'; // Amber for warning
    return 'app-accent-purple-bg-light border-border text-accent-foreground'; // Default accent
  };

  return (
    <div className="space-y-4">
      {/* Apply theme class */}
      <p className="text-muted-foreground mb-3 text-sm">
        Keep track of important tax deadlines to ensure you meet all your obligations to HMRC.
      </p>
      
      <div className="mb-4">
        {/* Apply theme class */}
        <h3 className="text-base font-medium text-accent-foreground mb-3">Upcoming Deadlines</h3>
        
        {upcomingEvents.length > 0 ? (
          <div className="space-y-2">
            {upcomingEvents.map((event, index) => (
              <div 
                key={`upcoming-${event.description}-${index}`}
                // Apply urgency classes
                className={`p-3 rounded-lg border ${getUrgencyClass(event.date)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <div>
                      {/* Text color is inherited from parent div */}
                      <p className="font-medium text-sm">{format(event.date, 'dd MMM yyyy')}</p>
                      <p className="text-xs opacity-90">{event.description}</p>
                    </div>
                  </div>
                  {/* Text color is inherited */}
                  <div className="text-xs font-medium whitespace-nowrap">
                    {differenceInDays(event.date, new Date())} days
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Apply theme class
          <p className="text-muted-foreground text-sm">No upcoming deadlines in the next 90 days.</p>
        )}
      </div>
      
      {/* Apply theme classes */}
      <Card className="app-bg-secondary border-border">
        <CardContent className="p-4">
          {/* Apply theme classes */}
          <h3 className="text-base font-medium text-accent-foreground mb-3 border-b border-border/50 pb-2">
            Tax Calendar
          </h3>
          
          <div className="space-y-3">
            {events.map((event, index) => (
              <div 
                key={`calendar-${event.description}-${index}`}
                // Apply theme classes, highlight today
                className={`p-2.5 rounded-lg border ${
                  isToday(event.date) 
                    ? 'border-accent-foreground bg-accent' // Highlight today with accent
                    : 'border-border app-bg-primary' // Use primary bg for items
                }`}
              >
                <div className="flex items-center gap-2">
                  {/* Apply theme classes */}
                  <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${isToday(event.date) ? 'bg-background/20 text-accent-foreground' : 'app-bg-secondary text-accent-foreground'}`}>
                    <div className="text-center leading-tight">
                      <div className="text-xs">{format(event.date, 'MMM')}</div>
                      <div className="font-bold">{format(event.date, 'd')}</div>
                    </div>
                  </div>
                  <div className="flex-1">
                    {/* Apply theme classes */}
                    <p className={`font-medium text-sm ${isToday(event.date) ? 'text-accent-foreground' : 'text-primary'}`}>
                      {format(event.date, 'dd MMM yyyy')}
                      {isToday(event.date) && (
                        // Apply theme classes
                        <span className="ml-2 inline-block app-accent-purple-bg text-white text-xs px-1.5 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                    </p>
                    {/* Apply theme classes */}
                    <p className={`text-xs ${isToday(event.date) ? 'text-accent-foreground/80' : 'text-muted-foreground'}`}>{event.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Apply theme classes */}
          <div className="mt-4 pt-3 border-t border-border/50">
            <h4 className="text-accent-foreground font-medium mb-2 flex items-center text-sm">
              <AlertCircle className="h-3 w-3 mr-1" />
              Important Notes
            </h4>
            {/* Apply theme class */}
            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5 pl-1">
              <li>Ensure you file your Self Assessment by 31st January to avoid penalties</li>
              <li>Keep up-to-date with any changes to tax deadlines from HMRC</li>
              <li>Set reminders several weeks before deadlines to ensure you have time to prepare</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxCalendar;
